/**
 * Platform foundations setup — team composition + taxonomy/location gates.
 * No Team entity: real AuthAccounts scored against slot mins.
 * @see docs/design/changes/20260908-UX-platform-setup-wizard.md
 */
import {
  CAPABILITY_PACK,
  JOB_ROLE,
  SEEDED_ROLE_NAMES,
  roleHasPack,
  type CapabilityPack,
} from '../identity/capability-packs.js';

export const SETUP_TEAM_SLOT_IDS = [
  'super_admin',
  'catalog',
  'buy',
  'warehouse',
  'sales',
  'finance',
] as const;

export type SetupTeamSlotId = (typeof SETUP_TEAM_SLOT_IDS)[number];

export type SetupTeamSlotDef = {
  id: SetupTeamSlotId;
  labelFr: string;
  min: number;
  /** True when this account's role names cover the slot. */
  matches: (roleNames: readonly string[]) => boolean;
};

export const SETUP_TEAM_COMPOSITION: readonly SetupTeamSlotDef[] = [
  {
    id: 'super_admin',
    labelFr: 'Super admin',
    min: 1,
    matches: (roles) =>
      roles.includes(JOB_ROLE.SUPER_ADMIN) || roleHasPack(roles, CAPABILITY_PACK.PARTY_ADMIN),
  },
  {
    id: 'catalog',
    labelFr: 'Catalogue',
    min: 1,
    matches: (roles) => roleHasPack(roles, CAPABILITY_PACK.CATALOG_MAINTAIN),
  },
  {
    id: 'buy',
    labelFr: 'Achat',
    min: 1,
    matches: (roles) => roleHasPack(roles, CAPABILITY_PACK.BUY_PROCURE),
  },
  {
    id: 'warehouse',
    labelFr: 'Entrepôt',
    min: 1,
    matches: (roles) => roleHasPack(roles, CAPABILITY_PACK.STOCK_MOVE),
  },
  {
    id: 'sales',
    labelFr: 'Vente',
    min: 1,
    matches: (roles) => roleHasPack(roles, CAPABILITY_PACK.SELL_COMMIT),
  },
  {
    id: 'finance',
    labelFr: 'Finance',
    min: 1,
    matches: (roles) =>
      roleHasPack(roles, CAPABILITY_PACK.MONEY_READ) ||
      roleHasPack(roles, CAPABILITY_PACK.MONEY_POST),
  },
];

export type TeamSlotStatus = {
  /** Pack slot id, or role name when scoring defined roles. */
  id: string;
  labelFr: string;
  min: number;
  count: number;
  met: boolean;
};

export type TeamCompositionStatus = {
  slots: readonly TeamSlotStatus[];
  /** All slot mins met (lone SA covers every pack slot). */
  slotsMet: boolean;
  /** At least two active staff (équipe ≠ bootstrap SA alone). */
  staffFloorMet: boolean;
  /** slotsMet ∧ staffFloorMet */
  met: boolean;
  activeStaffCount: number;
};

/** Min active staff for “équipe” (beyond seed SA). */
export const SETUP_TEAM_MIN_STAFF = 2;

function finalizeTeamStatus(
  slots: TeamSlotStatus[],
  activeStaffCount: number,
): TeamCompositionStatus {
  const slotsMet = slots.length === 0 ? false : slots.every((s) => s.met);
  const staffFloorMet = activeStaffCount >= SETUP_TEAM_MIN_STAFF;
  return {
    slots,
    slotsMet,
    staffFloorMet,
    met: slotsMet && staffFloorMet,
    activeStaffCount,
  };
}

/**
 * Score accounts (each = role name list) against pack composition table.
 * Used by people-cover-poles (operational poles), not setup Rôles→Équipe path.
 */
export function scoreTeamComposition(
  accountsRoleNames: readonly (readonly string[])[],
): TeamCompositionStatus {
  const activeStaffCount = accountsRoleNames.length;
  const slots: TeamSlotStatus[] = SETUP_TEAM_COMPOSITION.map((slot) => {
    let count = 0;
    for (const roles of accountsRoleNames) {
      if (slot.matches(roles)) count += 1;
    }
    return {
      id: slot.id,
      labelFr: slot.labelFr,
      min: slot.min,
      count,
      met: count >= slot.min,
    };
  });
  return finalizeTeamStatus(slots, activeStaffCount);
}

/**
 * Setup Équipe: one staffing row per defined role (templates and/or custom).
 * Min 1 account holding that role name. Empty role list → incomplete.
 */
export function scoreTeamByDefinedRoles(
  definedRoleNames: readonly string[],
  accountsRoleNames: readonly (readonly string[])[],
): TeamCompositionStatus {
  const activeStaffCount = accountsRoleNames.length;
  const slots: TeamSlotStatus[] = definedRoleNames.map((name) => {
    let count = 0;
    for (const roles of accountsRoleNames) {
      if (roles.includes(name)) count += 1;
    }
    return {
      id: name,
      labelFr: name,
      min: 1,
      count,
      met: count >= 1,
    };
  });
  return finalizeTeamStatus(slots, activeStaffCount);
}

/** Same coverage as historical poleCoverage.every — used by people-cover-poles. */
export function isTeamCompositionSlotsMet(
  accountsRoleNames: readonly (readonly string[])[],
): boolean {
  return scoreTeamComposition(accountsRoleNames).slotsMet;
}

export function isTeamCompositionMet(
  accountsRoleNames: readonly (readonly string[])[],
): boolean {
  return scoreTeamComposition(accountsRoleNames).met;
}

export type FoundationsSignals = {
  seededRoleCount: number;
  seededRoleExpected: number;
  /** Non-seeded roles (personalized create / bulk import). */
  customRoleCount: number;
  markeCount: number;
  productTypeCount: number;
  categoryCount: number;
  /** Catalog fiche field types (`ProductDetailType`). */
  productDetailTypeCount: number;
  /** Option groups that have ≥1 value (variant axes ready for matrix). */
  optionGroupWithValuesCount: number;
  /** Tag families that have ≥1 tag (product tag vocabulary ready). */
  tagFamilyWithValuesCount: number;
  locationCount: number;
  activeStaffCount: number;
  team: TeamCompositionStatus;
};

export type FoundationsStepId =
  | 'org_identity'
  | 'roles'
  | 'team'
  | 'markes'
  | 'structure'
  | 'fiches'
  | 'options'
  | 'tags'
  | 'locations'
  | 'pref_low_stock'
  | 'pref_near_expiry';

/** Templates installed, or at least one custom role defined. */
export function isRolesFoundationComplete(s: FoundationsSignals): boolean {
  const templatesOk =
    s.seededRoleExpected > 0 && s.seededRoleCount >= s.seededRoleExpected;
  const customOk = s.customRoleCount >= 1;
  return templatesOk || customOk;
}

export function isTeamFoundationComplete(s: FoundationsSignals): boolean {
  return s.team.met;
}

export function isMarkesFoundationComplete(s: FoundationsSignals): boolean {
  return s.markeCount >= 1;
}

export function isProductTypesFoundationComplete(s: FoundationsSignals): boolean {
  return s.productTypeCount >= 1;
}

export function isCategoriesFoundationComplete(s: FoundationsSignals): boolean {
  return s.categoryCount >= 1;
}

/** Product types + categories together (setup Structure step). */
export function isStructureFoundationComplete(s: FoundationsSignals): boolean {
  return isProductTypesFoundationComplete(s) && isCategoriesFoundationComplete(s);
}

/** All taxonomy facets (marque + type + category). */
export function isTaxonomyFoundationComplete(s: FoundationsSignals): boolean {
  return isMarkesFoundationComplete(s) && isStructureFoundationComplete(s);
}

export function isLocationsFoundationComplete(s: FoundationsSignals): boolean {
  return s.locationCount >= 1;
}

export function isProductDetailTypesFoundationComplete(s: FoundationsSignals): boolean {
  return s.productDetailTypeCount >= 1;
}

export function isOptionsFoundationComplete(s: FoundationsSignals): boolean {
  return s.optionGroupWithValuesCount >= 1;
}

export function isTagsFoundationComplete(s: FoundationsSignals): boolean {
  return s.tagFamilyWithValuesCount >= 1;
}

/** Soft preference steps — confirm/skip; never block Continuer / foundations. */
export function isSoftPreferenceStepComplete(_s: FoundationsSignals): boolean {
  return true;
}

/** @deprecated Use isSoftPreferenceStepComplete — kept for call sites. */
export function isPreferencesFoundationComplete(s: FoundationsSignals): boolean {
  return isSoftPreferenceStepComplete(s);
}

export function isFoundationStepComplete(
  step: FoundationsStepId,
  s: FoundationsSignals,
): boolean {
  switch (step) {
    case 'roles':
      return isRolesFoundationComplete(s);
    case 'team':
      return isTeamFoundationComplete(s);
    case 'markes':
      return isMarkesFoundationComplete(s);
    case 'structure':
      return isStructureFoundationComplete(s);
    case 'fiches':
      return isProductDetailTypesFoundationComplete(s);
    case 'options':
      return isOptionsFoundationComplete(s);
    case 'tags':
      return isTagsFoundationComplete(s);
    case 'locations':
      return isLocationsFoundationComplete(s);
    case 'org_identity':
    case 'pref_low_stock':
    case 'pref_near_expiry':
      return isSoftPreferenceStepComplete(s);
    default:
      return false;
  }
}

export function isFoundationsComplete(s: FoundationsSignals): boolean {
  return (
    isRolesFoundationComplete(s) &&
    isTeamFoundationComplete(s) &&
    isTaxonomyFoundationComplete(s) &&
    isProductDetailTypesFoundationComplete(s) &&
    isOptionsFoundationComplete(s) &&
    isTagsFoundationComplete(s) &&
    isLocationsFoundationComplete(s)
  );
}

export const FOUNDATIONS_STEP_IDS: readonly FoundationsStepId[] = [
  'org_identity',
  'roles',
  'team',
  'markes',
  'structure',
  'fiches',
  'options',
  'tags',
  'locations',
  'pref_low_stock',
  'pref_near_expiry',
] as const;

export function foundationsProgressPercent(s: FoundationsSignals): number {
  const done = FOUNDATIONS_STEP_IDS.filter((id) => isFoundationStepComplete(id, s)).length;
  return Math.round((100 * done) / FOUNDATIONS_STEP_IDS.length);
}

export function emptyFoundationsSignals(
  partial?: Partial<Omit<FoundationsSignals, 'team'>> & {
    team?: TeamCompositionStatus;
  },
): FoundationsSignals {
  const team =
    partial?.team ??
    scoreTeamComposition([]);
  return {
    seededRoleCount: partial?.seededRoleCount ?? 0,
    seededRoleExpected: partial?.seededRoleExpected ?? SEEDED_ROLE_NAMES.length,
    customRoleCount: partial?.customRoleCount ?? 0,
    markeCount: partial?.markeCount ?? 0,
    productTypeCount: partial?.productTypeCount ?? 0,
    categoryCount: partial?.categoryCount ?? 0,
    productDetailTypeCount: partial?.productDetailTypeCount ?? 0,
    optionGroupWithValuesCount: partial?.optionGroupWithValuesCount ?? 0,
    tagFamilyWithValuesCount: partial?.tagFamilyWithValuesCount ?? 0,
    locationCount: partial?.locationCount ?? 0,
    activeStaffCount: partial?.activeStaffCount ?? team.activeStaffCount,
    team,
  };
}

/** Build poleCoverage booleans from composition slots (compat for PeopleOrgSignals). */
export function poleCoverageFromTeam(team: TeamCompositionStatus): {
  catalog: boolean;
  buy: boolean;
  warehouse: boolean;
  sales: boolean;
  finance: boolean;
} {
  const byId = new Map(team.slots.map((s) => [s.id, s.met]));
  return {
    catalog: byId.get('catalog') ?? false,
    buy: byId.get('buy') ?? false,
    warehouse: byId.get('warehouse') ?? false,
    sales: byId.get('sales') ?? false,
    finance: byId.get('finance') ?? false,
  };
}

export type { CapabilityPack };
